import React from 'react';
import { toast } from 'sonner';
import { FileSpreadsheet, FileText, Image as ImageIcon } from 'lucide-react';
import { draftItem, soldLines } from '@/lib/tagSales';
import { exportSalesExcel, exportSalesPdf } from '@/lib/tagSalesExport';
import { printSaleImageList } from '@/lib/tagSaleImageList';
import { inr } from '@/lib/tagPricing';

// Saved rates (from an approval) on top of a fresh draft.
export const fromSaved = (x, sv) => (!sv ? x : {
  ...x,
  lines: x.lines.map((l) => { const m = sv.lines?.find((y) => y.label === l.label); return m ? { ...l, rate: m.rate } : l; }),
  labourRate: sv.labour_rate ?? x.labourRate,
  charges: x.charges.map((c) => { const m = sv.charges?.find((y) => y.name === c.name); return m ? { ...c, amount: m.amount } : c; }),
  // The piece's own gold rate on the approval (it may have been re-priced after the approval was made).
  ...(sv.gold_rate !== undefined && sv.gold_rate !== null ? { goldRate: sv.gold_rate } : {}),
});

// Rates the user changed against the rate card (labels → rate, labour), or null.
export function changedRates(drafts, card) {
  const out = { lines: {}, labour: null };
  drafts.forEach((x) => {
    const base = draftItem(x.data, card);
    x.lines.forEach((l, i) => { if (String(l.rate) !== String(base.lines[i]?.rate ?? '')) out.lines[l.label] = Number(l.rate) || 0; });
    if (String(x.labourRate) !== String(base.labourRate ?? '')) out.labour = Number(x.labourRate) || 0;
  });
  return Object.keys(out.lines).length || out.labour != null ? out : null;
}

const BTN = 'inline-flex h-9 items-center justify-center gap-1 rounded-lg border border-border bg-background text-xs font-semibold text-foreground hover:bg-secondary disabled:opacity-50';

// Excel / PDF / image list of a draft or saved approval (doc = { number, customer_name, created_at, items }).
// getLines: lines that each carry their own approval as l.sale (number / date / customer per piece).
// blocked: greyed out; a click calls onBlocked (e.g. to flag blank rates) instead of exporting.
export function ApprovalExports({ doc, thumbs, head, sub, pcs, testid = 'tagws-approval', multi = false, getLines, blocked = false, onBlocked }) {
  const lines = () => (getLines ? getLines() : soldLines([doc()]));
  const none = !doc().items.length;
  const go = (f) => () => (blocked ? onBlocked?.() : f());
  const cls = `${BTN} ${blocked ? 'cursor-not-allowed opacity-50' : ''}`;
  return (
    <div className="grid grid-cols-3 gap-1.5" data-testid={`${testid}-exports`}>
      <button type="button" disabled={none} aria-disabled={blocked} onClick={go(() => exportSalesExcel(head(), sub(), lines(), thumbs(), { showPieces: pcs, byCustomer: testid === 'tagws-approvals' }).catch(() => toast.error('Could not build the Excel file.')))} className={cls} data-testid={`${testid}-excel`}><FileSpreadsheet size={13} /> Excel</button>
      <button type="button" disabled={none} aria-disabled={blocked} onClick={go(() => exportSalesPdf(head(), sub(), lines(), thumbs(), { showPieces: pcs, byCustomer: testid === 'tagws-approvals' }))} className={cls} data-testid={`${testid}-pdf`}><FileText size={13} /> PDF</button>
      <button type="button" disabled={none} aria-disabled={blocked} onClick={go(() => printSaleImageList({ heading: 'Approval', name: doc().customer_name, period: sub(), lines: lines(), thumbs: thumbs(), withCustomer: multi, doc: 'Approval' }))} className={cls} data-testid={`${testid}-image-list`}><ImageIcon size={13} /> Images</button>
    </div>
  );
}

// "Apply changed rates to the rate card, or only to this approval?"
export function ApplyRatesAsk({ ask, cardName, onBack, onOnly, onApply }) {
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 p-4" data-testid="tagws-approval-apply-dialog">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-2xl animate-in fade-in zoom-in-95 duration-150">
        <div className="text-base font-semibold text-foreground">You changed some rates</div>
        <p className="mt-2 text-sm text-muted-foreground">Apply them to the rate card <b className="text-foreground">{cardName}</b> for everyone, or keep them only for this approval?</p>
        <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto rounded-lg bg-secondary/40 p-2 font-mono text-xs">
          {Object.entries(ask.lines).map(([k, v]) => <li key={k} className="flex justify-between gap-2"><span className="truncate">{k}</span><span>Rs. {inr(v)}</span></li>)}
          {ask.labour != null ? <li className="flex justify-between"><span>Labour / g</span><span>Rs. {inr(ask.labour)}</span></li> : null}
        </ul>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onBack} className="h-9 rounded-lg px-3 text-sm text-muted-foreground hover:text-foreground" data-testid="tagws-approval-apply-cancel">Back</button>
          <button type="button" onClick={onOnly} className="h-9 rounded-lg border border-border px-3 text-sm font-semibold hover:bg-secondary" data-testid="tagws-approval-apply-only">Only this approval</button>
          <button type="button" onClick={onApply} className="h-9 rounded-lg bg-violet-700 px-3 text-sm font-semibold text-white hover:bg-violet-800" data-testid="tagws-approval-apply-card">Update rate card</button>
        </div>
      </div>
    </div>
  );
}
